/* eslint-disable react/prop-types -- this codebase does not use propTypes (see CLAUDE.md). */
import { useEffect, useRef, useState } from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import { Modal } from "../../components/ui/Modal";
import PassPreview, { StampStrip } from "../passes/components/PassPreview";
import { cx } from "./ui";

// Type-specific line shown in the strip of non-stamp cards (the stamp card shows
// its stamps instead).
function StripBand({ formData, label }) {
  return (
    <div
      className="flex w-full items-center justify-center px-6 text-center text-sm font-semibold"
      style={{ aspectRatio: "1140/435", backgroundColor: formData.textColor, color: formData.backgroundColor }}
    >
      {label}
    </div>
  );
}

// The card as it will look in the Wallet, small enough to stay on screen while
// the merchant edits below it. `compact` (while typing, so the keyboard leaves
// room for the field) shrinks it to its header row.
export function CardPreviewFront({ formData, stampIconSource, bandLabel, compact }) {
  const preview = formData.preview || {};
  return (
    <div
      className="w-full overflow-hidden rounded-xl shadow-md ring-1 ring-black/5"
      style={{ backgroundColor: formData.backgroundColor, color: formData.textColor }}
      translate="no"
    >
      <div className="flex items-center justify-between gap-2 px-3 py-2">
        <div className="flex min-w-0 items-center gap-2">
          {formData.companyLogo ? (
            <img src={formData.companyLogo} alt="" className="h-8 w-8 shrink-0 rounded-full object-cover" />
          ) : (
            <div className="h-8 w-8 shrink-0 rounded-full bg-white/30" />
          )}
          <span className="truncate text-sm font-semibold">{formData.company}</span>
        </div>
      </div>
      {!compact && (
        <>
          {formData.type === "stamp" ? (
            <StampStrip formData={formData} getStampIconSource={() => stampIconSource} />
          ) : (
            <StripBand formData={formData} label={bandLabel} />
          )}
          <div className="flex items-start justify-between gap-3 px-3 py-2.5">
            <div className="min-w-0">
              <div className="text-[10px] font-semibold uppercase tracking-wide opacity-80">
                {preview.leftField?.label}
              </div>
              <div className="truncate text-sm">{preview.leftField?.value}</div>
            </div>
            <div className="min-w-0 text-right">
              <div className="text-[10px] font-semibold uppercase tracking-wide opacity-80">
                {preview.rightField?.label}
              </div>
              <div className="truncate text-sm">{preview.rightField?.value}</div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// The dashboard's full preview (front/back, iOS/Android) in a dialog, scaled
// down to fit narrow phones: it is laid out at a fixed 380px.
export function FullCardDialog({ open, onClose, formData, stampIconSource }) {
  const { t } = useLanguage();
  const boxRef = useRef(null);
  const innerRef = useRef(null);
  const [size, setSize] = useState({ scale: 1, height: 0 });

  useEffect(() => {
    if (!open) return undefined;
    const measure = () => {
      const width = boxRef.current?.clientWidth || 380;
      const scale = Math.min(1, width / 428);
      setSize({ scale, height: (innerRef.current?.offsetHeight || 0) * scale });
    };
    const id = requestAnimationFrame(measure);
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(measure) : null;
    if (observer && innerRef.current) observer.observe(innerRef.current);
    window.addEventListener("resize", measure);
    return () => {
      cancelAnimationFrame(id);
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [open]);

  return (
    <Modal open={open} onClose={onClose} title={t("start.customize.preview.fullTitle")}>
      <div ref={boxRef} className="w-full overflow-hidden" style={{ height: size.height || undefined }}>
        <div ref={innerRef} style={{ width: 428, transform: `scale(${size.scale})`, transformOrigin: "top left" }}>
          <PassPreview formData={formData} getStampIconSource={() => stampIconSource} />
        </div>
      </div>
      <button
        type="button"
        onClick={onClose}
        className="min-h-11 w-full rounded-xl border border-gray-300 font-semibold text-gray-900"
      >
        {t("start.customize.preview.close")}
      </button>
    </Modal>
  );
}

// Full-size dashboard preview mounted far off screen: the source the card
// images are rendered from. Must stay unscaled and visible to html2canvas.
export function CaptureSource({ rootRef, stripRef, formData, stampIconSource }) {
  return (
    <div
      ref={rootRef}
      aria-hidden="true"
      className={cx("pointer-events-none")}
      style={{ position: "absolute", left: "-99999px", top: 0, width: 440 }}
    >
      <PassPreview formData={formData} previewRef={stripRef} getStampIconSource={() => stampIconSource} />
    </div>
  );
}
